import { useMemo } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Controller } from 'react-hook-form';

import { FormControl } from '../../../FormControl';
import { FormTextArea } from '../../../FormTextArea';
import { Select, SelectOption } from '../../../Select';
import { Typography } from '../../../Typography';
import type { ReviewOptionType } from '../../../VotingDropdownMenu';
import styles from './ReportDialog.module.scss';

const MAX_COMMENT_LENGTH = 400;

export interface ReportCommentStepProps {
  reviewReportTypes: ReviewOptionType[];
  form: UseFormReturn<{
    reportText: string;
    reportType: string;
  }>;
}

const ReportDialogContent = ({ reviewReportTypes, form }: ReportCommentStepProps) => {
  const { control } = form;

  const reviewReportTypesMap = useMemo(() => {
    return Object.fromEntries(
      reviewReportTypes.map(({ id, title, description }) => [id, { title, description }]),
    );
  }, [reviewReportTypes]);

  return (
    <form className={styles['comment__container']}>
      <Controller
        control={control}
        name="reportType"
        render={({ field }) => (
          <FormControl label="Reason for the Report">
            <Select onValueChange={field.onChange} value={field.value} portalMode="overlay">
              {reviewReportTypes.map(({ id, title }) => (
                <SelectOption key={id} value={id}>
                  {title}
                </SelectOption>
              ))}
            </Select>
            <Typography variant="caption-s" color="tertiary">
              {reviewReportTypesMap[field.value]?.description}
            </Typography>
          </FormControl>
        )}
      />
      <Controller
        control={control}
        name="reportText"
        rules={{
          required: 'This field is required',
          maxLength: {
            value: MAX_COMMENT_LENGTH,
            message: `Comment should be less than ${MAX_COMMENT_LENGTH} symbols`,
          },
        }}
        render={({ field, fieldState }) => (
          <FormTextArea
            {...field}
            label="Comment"
            isError={!!fieldState.error}
            hint={fieldState.error?.message}
            maxLength={MAX_COMMENT_LENGTH}
            rows={4}
          />
        )}
      />
    </form>
  );
};

export { ReportDialogContent };
